import { resolveAvatarUrl, resolveIconUrl, resolveLogoUrl } from "./assets";
import type {
  FooterConfig,
  FooterContentPart,
  FooterDisplayFlags,
  FooterMode,
} from "./types";
import { DEFAULT_ASSETS_BASE, DEFAULT_CLIENTS_BASE, DEFAULT_LABEL_AFTER, MODE_DISPLAY } from "./types";

export function normalizeMode(config: Pick<FooterConfig, "mode" | "variant">): FooterMode {
  const candidate = config.mode ?? config.variant;

  if (candidate === "logo" || candidate === "text") {
    return candidate;
  }

  if (
    candidate === "logo-text" ||
    candidate === "icon" ||
    candidate === "icon-text" ||
    candidate === "avatar" ||
    candidate === "avatar-text" ||
    candidate === "custom"
  ) {
    return candidate;
  }

  return "logo";
}

export function resolveDisplayFlags(config: FooterConfig): Required<FooterDisplayFlags> {
  const mode = normalizeMode(config);
  const base =
    mode === "custom"
      ? {
          showLogo: false,
          showText: false,
          showAvatar: false,
          showIcon: false,
        }
      : MODE_DISPLAY[mode];

  const override = config.display ?? {};

  return {
    showLogo: override.showLogo ?? base.showLogo,
    showText: override.showText ?? base.showText,
    showAvatar: override.showAvatar ?? base.showAvatar,
    showIcon: override.showIcon ?? base.showIcon,
  };
}

export function buildContentParts(
  config: FooterConfig,
  theme: "light" | "dark"
): FooterContentPart[] {
  const flags = resolveDisplayFlags(config);
  const parts: FooterContentPart[] = [];
  const mode = normalizeMode(config);

  const assetsBase = config.assetsBase || DEFAULT_ASSETS_BASE;
  const clientsBase = config.clientsBase || DEFAULT_CLIENTS_BASE;

  const labelBefore = config.labelBefore?.trim();
  if (labelBefore) {
    parts.push({ type: "text", text: labelBefore, placement: "before" });
  }

  if (flags.showAvatar) {
    parts.push({
      type: "avatar",
      src: resolveAvatarUrl(assetsBase, clientsBase, config.assets, config.clientId),
      alt: "Pandorium",
    });
  }

  if (flags.showIcon) {
    parts.push({
      type: "icon",
      src: resolveIconUrl(assetsBase, clientsBase, config.assets, config.clientId),
      alt: "Pandorium",
    });
  }

  if (flags.showLogo) {
    parts.push({
      type: "logo",
      src: resolveLogoUrl(assetsBase, theme, config.assets),
      alt: "Pandorium",
    });
  }

  const labelAfter = config.labelAfter?.trim();
  if (labelAfter) {
    parts.push({ type: "text", text: labelAfter, placement: "after" });
  } else if (mode === "text" && !labelBefore && flags.showText) {
    parts.push({
      type: "text",
      text: DEFAULT_LABEL_AFTER,
      placement: "after",
    });
  }

  return parts;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
